import { useEffect, useState } from 'react';
import {
  buildBasicAuth,
  clearAdminAuth,
  loadAppRelease,
  readAdminAuth,
  saveAdminAuth,
  uploadApk,
} from '../adminApi';
import AppShell from '../components/AppShell';
import PrimaryButton from '../components/PrimaryButton';

function AdminPage() {
  const [authHeader, setAuthHeader] = useState(readAdminAuth());
  const [release, setRelease] = useState(null);
  const [errorText, setErrorText] = useState('');
  const [successText, setSuccessText] = useState('');
  const [isLoading, setIsLoading] = useState(Boolean(authHeader));

  useEffect(() => {
    if (!authHeader) {
      return;
    }

    setIsLoading(true);
    loadAppRelease(authHeader)
      .then((data) => {
        setRelease(data);
        setErrorText('');
      })
      .catch((error) => {
        clearAdminAuth();
        setAuthHeader(null);
        setErrorText(error.message);
      })
      .finally(() => setIsLoading(false));
  }, [authHeader]);

  function handleLogout() {
    clearAdminAuth();
    setAuthHeader(null);
    setRelease(null);
    setSuccessText('');
    setErrorText('');
  }

  return (
    <AppShell>
      <section className="hero compact-hero admin-hero">
        <div className="hero-badge">APK Admin</div>
        <h1>Панель обновления приложения</h1>
        <p>
          Доступ ограничен логином и паролем. Здесь можно только загрузить новую APK
          и проверить текущую опубликованную версию.
        </p>
      </section>

      {!authHeader ? (
        <LoginForm
          errorText={errorText}
          onLogin={async (username, password) => {
            const nextAuthHeader = buildBasicAuth(username, password);
            setIsLoading(true);
            setErrorText('');
            try {
              const data = await loadAppRelease(nextAuthHeader);
              saveAdminAuth(nextAuthHeader);
              setAuthHeader(nextAuthHeader);
              setRelease(data);
            } catch (error) {
              setErrorText(error.message);
            } finally {
              setIsLoading(false);
            }
          }}
          isLoading={isLoading}
        />
      ) : (
        <ApkAdminPanel
          authHeader={authHeader}
          release={release}
          errorText={errorText}
          successText={successText}
          isLoading={isLoading}
          onLogout={handleLogout}
          onUploaded={(data) => {
            setRelease(data);
            setSuccessText('APK успешно загружена.');
            setErrorText('');
          }}
          onError={(message) => {
            setErrorText(message);
            setSuccessText('');
          }}
          setIsLoading={setIsLoading}
        />
      )}
    </AppShell>
  );
}

function LoginForm({ errorText, isLoading, onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  return (
    <form
      className="admin-card"
      onSubmit={(event) => {
        event.preventDefault();
        onLogin(username, password);
      }}
    >
      <label>
        Логин
        <input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" />
      </label>
      <label>
        Пароль
        <input
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          type="password"
          autoComplete="current-password"
        />
      </label>
      {errorText && <div className="admin-message admin-message-error">{errorText}</div>}
      <PrimaryButton type="submit">{isLoading ? 'Проверяем...' : 'Войти'}</PrimaryButton>
    </form>
  );
}

function ApkAdminPanel({
  authHeader,
  release,
  errorText,
  successText,
  isLoading,
  onLogout,
  onUploaded,
  onError,
  setIsLoading,
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  function selectApkFile(file) {
    if (!file) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.apk')) {
      onError('Можно загрузить только APK файл.');
      return;
    }
    setSelectedFile(file);
    onError('');
  }

  async function handleUpload(event) {
    event.preventDefault();
    if (!selectedFile) {
      onError('Выберите APK файл.');
      return;
    }

    setIsLoading(true);
    try {
      const data = await uploadApk(authHeader, selectedFile);
      onUploaded(data);
      setSelectedFile(null);
    } catch (error) {
      onError(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="admin-card">
      <div className="admin-card-header">
        <div>
          <h2>Актуальная APK</h2>
          <p>{release?.apkUploaded ? 'Файл загружен и доступен пользователям.' : 'APK ещё не загружена.'}</p>
        </div>
        <button className="admin-link-button" type="button" onClick={onLogout}>
          Выйти
        </button>
      </div>

      <div className="release-grid">
        <ReleaseInfo label="Application ID" value={release?.applicationId || 'Нет данных'} />
        <ReleaseInfo label="Version Code" value={release?.versionCode || '0'} />
        <ReleaseInfo label="Version Name" value={release?.versionName || 'Нет данных'} />
        <ReleaseInfo label="Обязательное обновление" value={release?.forceUpdateEnabled ? 'Включено' : 'Выключено'} />
      </div>

      <form className="upload-form" onSubmit={handleUpload}>
        <label
          className={`apk-drop-zone ${isDragging ? 'apk-drop-zone-active' : ''}`}
          onDragEnter={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setIsDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setIsDragging(false);
            selectApkFile(event.dataTransfer.files?.[0]);
          }}
        >
          <input
            className="apk-file-input"
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            onChange={(event) => selectApkFile(event.target.files?.[0])}
          />
          <span className="apk-drop-icon">APK</span>
          <strong>Перетащите APK сюда</strong>
          <small>или нажмите, чтобы выбрать файл вручную</small>
        </label>
        {selectedFile && <div className="admin-file-name">{selectedFile.name}</div>}
        {errorText && <div className="admin-message admin-message-error">{errorText}</div>}
        {successText && <div className="admin-message admin-message-success">{successText}</div>}
        <PrimaryButton type="submit">{isLoading ? 'Загружаем...' : 'Загрузить APK'}</PrimaryButton>
      </form>
    </section>
  );
}

function ReleaseInfo({ label, value }) {
  return (
    <div className="release-info">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

export default AdminPage;
